import i18n from '@/utils/i18n';
import PlusIcon from '@assets/icons/plus.svg';
import React from 'react';
import { AnimatedGradient } from '@usepepper/ui/atoms/animated-gradient';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, shadow, TYPOGRAPHY } from '@/styles';
import { LinearGradientText } from '@usepepper/ui/atoms/linear-gradient-text';
import { SegmentedControl } from '@usepepper/ui/molecules/segmented-control';
import { Text, TouchableOpacity, View } from 'react-native';

export enum ChatTab {
  Chatbot = 'CHATBOT',
  Customer = 'CUSTOMER',
}

interface ChatTabsProps {
  isNewSession: boolean;
  onPressChatbot: () => void;
  onPressCustomer: () => void;
  onPressNewSession: () => void;
  selectedTab: ChatTab;
}

export const ChatTabs = ({
  isNewSession,
  onPressChatbot,
  onPressCustomer,
  onPressNewSession,
  selectedTab,
}: ChatTabsProps) => {
  const [styles, theme] = useStyles();

  const chatBotGradientArray: [string, string] = ['#E9F7FA', '#EAEEFD'];
  const chatBotBaseGradientArray: [string, string] = [
    theme.colors.background,
    theme.colors.background,
  ];
  const chatBotTextGradientArray: [string, string] = ['#5DC3E8', '#7562F5'];

  // segmented control children
  const leftOption = (
    <Text style={styles.chatTabText}>{i18n.t('customer')}</Text>
  );
  const rightOption =
    selectedTab === ChatTab.Chatbot ? (
      <LinearGradientText
        colors={chatBotTextGradientArray}
        numberOfLines={1}
        style={styles.chatTabText}
      >
        {i18n.t('casey_ai')}
      </LinearGradientText>
    ) : (
      <Text style={styles.chatTabText}>{i18n.t('casey_ai')}</Text>
    );

  return (
    <View style={styles.chatTabsContainer}>
      <AnimatedGradient
        colors={
          selectedTab === ChatTab.Chatbot
            ? chatBotGradientArray
            : chatBotBaseGradientArray
        }
        isAnimationTriggered={selectedTab === ChatTab.Chatbot}
        style={styles.chatTabBackground}
      >
        <SegmentedControl
          options={[
            {
              accessibilityLabel: i18n.t('customer'),
              children: leftOption,
              key: ChatTab.Customer,
              onPress: onPressCustomer,
            },
            {
              accessibilityLabel: i18n.t('casey_ai'),
              children: rightOption,
              key: ChatTab.Chatbot,
              onPress: onPressChatbot,
            },
          ]}
          selectedOption={selectedTab}
        />
      </AnimatedGradient>
      {isNewSession ? (
        <View style={styles.newSessionView}>
          <TouchableOpacity
            onPress={onPressNewSession}
            style={[styles.chatTabBaseButton, styles.chatTabButton]}
          >
            <PlusIcon
              fill={theme.colors.black}
              height={20}
              style={styles.newSessionIcon}
              width={20}
            />
            <Text style={styles.chatTabText}>{i18n.t('new_session')}</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  chatTabBackground: {
    backgroundColor: theme.colors.stroke,
    borderRadius: 40,
    flexDirection: 'row',
    height: 40,
    padding: 4,
  },
  chatTabBaseButton: {
    alignItems: 'center',
    borderRadius: 24,
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  chatTabButton: {
    backgroundColor: theme.colors.white,
    borderRadius: 24,
    ...shadow({
      color: theme.colors.black,
      elevation: 1,
      opacity: 0.1,
      radius: 8,
      x: 0,
      y: 4,
    }),
  },
  chatTabsContainer: {
    alignSelf: 'center',
    position: 'absolute',
    top: 8,
    width: 200,
  },
  chatTabText: {
    ...TYPOGRAPHY.medium,
    ...FONTS.primarySemiBold,
    fontSize: 14,
  },
  newSessionIcon: {
    marginRight: 8,
  },
  newSessionView: {
    alignSelf: 'center',
    height: 30,
    marginTop: 12,
    maxWidth: 180,
    width: '75%',
  },
}));
